import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PullResult, Rarity } from '@tarot/contracts';
import { Badge } from '@/components/ui';
import { platform } from '@/platform';

const icon = (r: PullResult) =>
  r.kind === 'deck' ? String(r.render.symbol ?? '🂠') : String(r.render.emoji || ({ cloth: '🟪', pattern: '🔶', candles: '🕯️', light: '🌌' } as Record<string, string>)[r.slot ?? ''] || '✦');
const best = (rs: PullResult[]): Rarity => (rs.some((r) => r.rarity === 'event') ? 'event' : rs.some((r) => r.rarity === 'rare') ? 'rare' : 'common');

/** Полноэкранная анимация результата крутки: вспышка по редкости, раскрытие по одному, «Пропустить». */
export function PullOverlay({ results, onClose }: { results: PullResult[]; onClose: () => void }) {
  const [revealed, setRevealed] = useState(results.length === 1 ? 0 : 0);
  const [showCard, setShowCard] = useState(false);
  const top = best(results);

  useEffect(() => {
    platform.haptic(top === 'event' ? 'heavy' : top === 'rare' ? 'medium' : 'light');
    if (results.length === 1) { const t = setTimeout(() => setShowCard(true), 400); return () => clearTimeout(t); }
    if (revealed < results.length) {
      const t = setTimeout(() => { if (results[revealed]!.rarity !== 'common') platform.haptic('medium'); setRevealed((n) => n + 1); }, 280);
      return () => clearTimeout(t);
    }
  }, [revealed, results, top]);

  const single = results[0]!;
  return createPortal(
    <div className="pull-overlay">
      <div className={`flash ${top}`} />
      {results.length === 1 ? (
        showCard && (
          <div className={`pull-card ${single.rarity}`}>
            <Badge rarity={single.rarity} />
            <div className="big">{icon(single)}</div>
            <h2>{single.title}</h2>
            <p className="muted">{single.duplicate ? `Дубликат → +${single.shards} 🔷 осколков` : single.kind === 'deck' ? 'Новая колода!' : 'Новый предмет!'}</p>
            <button className="btn primary" style={{ marginTop: 10 }} onClick={onClose}>Забрать</button>
          </div>
        )
      ) : (
        <>
          <h2 style={{ marginBottom: 12 }}>Результаты</h2>
          <div className="pull-grid">
            {results.slice(0, revealed).map((r, i) => (
              <div key={i} className={`pull-mini ${r.rarity}`} style={{ position: 'relative' }} title={r.title}>
                {icon(r)}{r.duplicate && <span className="dup">+{r.shards}</span>}
              </div>
            ))}
          </div>
          <button className="btn primary" style={{ maxWidth: 300, marginTop: 16 }} onClick={() => (revealed < results.length ? setRevealed(results.length) : onClose())}>
            {revealed < results.length ? 'Пропустить' : 'Забрать всё'}
          </button>
          <p className="dim" style={{ marginTop: 8 }}>Редких: {results.filter((r) => r.rarity !== 'common').length} · дубликаты → осколки</p>
        </>
      )}
    </div>,
    document.body,
  );
}
